import React from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { useFormik } from "formik";
import useAuth from "../../../hooks/useAuth";
import { useParams } from "react-router-dom";
import {
  createDesignationPermissions,
  fetchAllPermissionsByDesignation,
} from "../../../services/api/designation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ModuleType } from "../../../utils/enum";
import { getDesignationPermissionsByModule } from "../../../utils/getDesignationPermissionsByModule";

const Invoices = ({
  designationPermissions,
  designationId,
  refetchAllPermissionsByDesignation,
}) => {
  const customerInvoicesPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.CUSTOMER_INVOICE
  );
  const engineerInvoicesPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.ENGINEER_INVOICE
  );
  const customerInvoiceVerifyActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_VERIFY_CUSTOMER_INVOICE
    );

  const engineerInvoiceVerifyActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_VERIFY_ENGINEER_INVOICE
    );

  const customerInvoiceSendActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_SEND_CUSTOMER_INVOICE
    );

  const engineerInvoiceSendActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_SEND_ENGINEER_INVOICE
    );

  const customerInvoicePaidActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_PAID_CUSTOMER_INVOICE
    );

  const engineerInvoicePaidActionPermissions =
    getDesignationPermissionsByModule(
      designationPermissions,
      ModuleType.ACTION_PAID_ENGINEER_INVOICE
    );

  const {
    isPending: isCreateDesignationPermissionsApiLoading,
    error: isCreateDesignationPermissionsApiError,
    mutateAsync: createDesignationPermissionsMutation,
  } = useMutation({
    mutationFn: createDesignationPermissions,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      CUSTOMER_INVOICE: {
        isRead: customerInvoicesPermissions.hasReadAccess,
        isWrite: customerInvoicesPermissions.hasWriteAccess,
        isUpdate: customerInvoicesPermissions.hasUpdateAccess,
        isDelete: customerInvoicesPermissions.hasDeleteAccess,
      },
      ENGINEER_INVOICE: {
        isRead: engineerInvoicesPermissions.hasReadAccess,
        isWrite: engineerInvoicesPermissions.hasWriteAccess,
        isUpdate: engineerInvoicesPermissions.hasUpdateAccess,
        isDelete: engineerInvoicesPermissions.hasDeleteAccess,
      },
      ACTION_VERIFY_CUSTOMER_INVOICE: {
        isWrite: customerInvoiceVerifyActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_VERIFY_ENGINEER_INVOICE: {
        isWrite: engineerInvoiceVerifyActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_SEND_CUSTOMER_INVOICE: {
        isWrite: customerInvoiceSendActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_SEND_ENGINEER_INVOICE: {
        isWrite: engineerInvoiceSendActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_PAID_CUSTOMER_INVOICE: {
        isWrite: customerInvoicePaidActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_PAID_ENGINEER_INVOICE: {
        isWrite: engineerInvoicePaidActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
    },
    onSubmit: (values) => {
      // Replace with your actual submit handler
      // handleConfirmApprovalButton(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleCustomerInvoicesPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.CUSTOMER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };
  const handleEngineerInvoicesPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ENGINEER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleVerifyCustomerInvoicePermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_VERIFY_CUSTOMER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleVerifyEngineerInvoicePermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_VERIFY_ENGINEER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleSendEngineerInvoicePermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_SEND_ENGINEER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleSendCustomerInvoicePermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_SEND_CUSTOMER_INVOICE,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  return (
    <BaseCard elevation={0} className="p-5 rounded-lg">
      <div className="border-b pb-4">
        <p className="text-dark text-base font-normal mb-2">
          Customer Invoices
        </p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Read Invoice"
            name="CUSTOMER_INVOICE.isRead"
            checked={formik.values.CUSTOMER_INVOICE.isRead}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isRead",
                ModuleType.CUSTOMER_INVOICE,
                customerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Invoice"
            name="CUSTOMER_INVOICE.isWrite"
            checked={formik.values.CUSTOMER_INVOICE.isWrite}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isWrite",
                ModuleType.CUSTOMER_INVOICE,
                customerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Edit Invoice"
            name="CUSTOMER_INVOICE.isUpdate"
            checked={formik.values.CUSTOMER_INVOICE.isUpdate}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isUpdate",
                ModuleType.CUSTOMER_INVOICE,
                customerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Delete Invoice"
            name="CUSTOMER_INVOICE.isDelete"
            checked={formik.values.CUSTOMER_INVOICE.isDelete}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isDelete",
                ModuleType.CUSTOMER_INVOICE,
                customerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Verify Invoice"
            name="ACTION_VERIFY_CUSTOMER_INVOICE.isWrite"
            checked={formik.values.ACTION_VERIFY_CUSTOMER_INVOICE.isWrite}
            onChange={(e) =>
              handleVerifyCustomerInvoicePermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_VERIFY_CUSTOMER_INVOICE,
                customerInvoiceVerifyActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Send Invoice"
            name="ACTION_SEND_CUSTOMER_INVOICE.isWrite"
            checked={formik.values.ACTION_SEND_CUSTOMER_INVOICE.isWrite}
            onChange={(e) =>
              handleSendCustomerInvoicePermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_SEND_CUSTOMER_INVOICE,
                customerInvoiceSendActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Paid Invoice"
            name="ACTION_PAID_CUSTOMER_INVOICE.isDelete"
            checked={formik.values.ACTION_PAID_CUSTOMER_INVOICE.isWrite}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_PAID_CUSTOMER_INVOICE,
                customerInvoicePaidActionPermissions.permissionId
              )
            }
          />
        </div>
      </div>

      <div className="border-b pb-4 mt-2">
        <p className="text-dark text-base font-normal mb-2">
          Engineer Invoices
        </p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Read Invoice"
            name="ENGINEER_INVOICE.isRead"
            checked={formik.values.ENGINEER_INVOICE.isRead}
            onChange={(e) =>
              handleEngineerInvoicesPermissionsChange(
                e,
                "isRead",
                ModuleType.ENGINEER_INVOICE,
                engineerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Invoice"
            name="ENGINEER_INVOICE.isWrite"
            checked={formik.values.ENGINEER_INVOICE.isWrite}
            onChange={(e) =>
              handleEngineerInvoicesPermissionsChange(
                e,
                "isWrite",
                ModuleType.ENGINEER_INVOICE,
                engineerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Edit Invoice"
            name="ENGINEER_INVOICE.isUpdate"
            checked={formik.values.ENGINEER_INVOICE.isUpdate}
            onChange={(e) =>
              handleEngineerInvoicesPermissionsChange(
                e,
                "isUpdate",
                ModuleType.ENGINEER_INVOICE,
                engineerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Delete Invoice"
            name="ENGINEER_INVOICE.isDelete"
            checked={formik.values.ENGINEER_INVOICE.isDelete}
            onChange={(e) =>
              handleEngineerInvoicesPermissionsChange(
                e,
                "isDelete",
                ModuleType.ENGINEER_INVOICE,
                engineerInvoicesPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Verify Invoice"
            name="ACTION_VERIFY_ENGINEER_INVOICE.isWrite"
            checked={formik.values.ACTION_VERIFY_ENGINEER_INVOICE.isWrite}
            onChange={(e) =>
              handleVerifyEngineerInvoicePermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_VERIFY_ENGINEER_INVOICE,
                engineerInvoiceVerifyActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Send Invoice"
            name="ACTION_SEND_ENGINEER_INVOICE.isWrite"
            checked={formik.values.ACTION_SEND_ENGINEER_INVOICE.isWrite}
            onChange={(e) =>
              handleSendEngineerInvoicePermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_SEND_ENGINEER_INVOICE,
                engineerInvoiceSendActionPermissions.permissionId
              )
            }
          />

          <BaseCheckbox
            label="Paid Invoice"
            name="ACTION_PAID_ENGINEER_INVOICE.isWrite"
            checked={formik.values.ACTION_PAID_ENGINEER_INVOICE.isWrite}
            onChange={(e) =>
              handleCustomerInvoicesPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_PAID_ENGINEER_INVOICE,
                engineerInvoicePaidActionPermissions.permissionId
              )
            }
          />
        </div>
      </div>
    </BaseCard>
  );
};

export default Invoices;
